import { useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, 
  Settings, 
  Users, 
  BookOpen, 
  AlertTriangle,
  UserCog,
  LogOut,
  Shield,
  Menu
} from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import Loader from './Loader';

const sidebarLinks = [
  { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { path: '/admin/settings', label: 'Paramètres', icon: Settings },
  { path: '/admin/staff', label: 'Staff', icon: Users },
  { path: '/admin/rules', label: 'Règlement', icon: BookOpen },
  { path: '/admin/incidents', label: 'Incidents', icon: AlertTriangle },
];

const superAdminLinks = [
  { path: '/admin/users', label: 'Administrateurs', icon: UserCog },
];

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { admin, loading, isAuthenticated, isSuperAdmin, logout } = useAuth();
  const { settings } = useSettings();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location]);

  if (loading) {
    return <Loader fullScreen text="Authentification..." />;
  }

  if (!isAuthenticated) {
    return null;
  }

  const allLinks = isSuperAdmin 
    ? [...sidebarLinks, ...superAdminLinks]
    : sidebarLinks;

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-[#0a0a0a] border-r border-white/5
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/5">
            <Link to="/admin" className="flex items-center gap-3" data-testid="admin-logo">
              {settings.logo_url ? (
                <img 
                  src={settings.logo_url} 
                  alt={settings.server_name}
                  className="h-8 w-auto"
                />
              ) : (
                <>
                  <Shield className="w-8 h-8 text-violet-500" />
                  <span className="font-heading font-bold text-lg uppercase tracking-wider gradient-text">
                    Admin
                  </span>
                </>
              )}
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {allLinks.map((link) => {
              const Icon = link.icon;
              const isActive = link.exact 
                ? location.pathname === link.path
                : location.pathname.startsWith(link.path);
              
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`admin-nav-${link.path.replace('/admin/', '') || 'dashboard'}`}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200
                    ${isActive 
                      ? 'bg-violet-600/20 text-violet-400 border border-violet-500/30' 
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{link.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* User Info & Logout */}
          <div className="p-4 border-t border-white/5">
            <div className="flex items-center gap-3 mb-4 px-2">
              {admin?.discord_avatar ? (
                <img 
                  src={admin.discord_avatar} 
                  alt={admin.username}
                  className="w-10 h-10 rounded-full border-2 border-violet-500/50"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-violet-600/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-violet-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{admin?.username}</p>
                <p className="text-xs text-violet-400 uppercase tracking-wider">{admin?.role}</p>
              </div>
            </div>
            
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Déconnexion</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Bar (Mobile) */}
        <header className="lg:hidden glass-heavy p-4 flex items-center justify-between">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="p-2 text-gray-400 hover:text-white"
            data-testid="admin-mobile-menu"
          >
            <Menu className="w-6 h-6" />
          </button>
          <span className="font-heading font-bold uppercase tracking-wider gradient-text">
            Admin Panel
          </span>
          <div className="w-10" /> {/* Spacer */}
        </header>

        {/* Page Content */}
        <motion.main 
          className="flex-1 p-4 lg:p-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Outlet />
        </motion.main>
      </div>
    </div>
  );
};

export default AdminLayout;
